import { Injectable } from "@angular/core";
import { Observable, combineLatest, throwError } from "rxjs";
import moment from "moment";
import { map, switchMap } from "rxjs/operators";
import { AdvisorFacade, BrandingService, TimeHelper, defaultCalendarLinkUrl, AdvisorWithDisplayPhone } from "common";
import { BrokerUserData } from "projects/partner/src/app/broker/domain/models/broker.model";
import { DashboardBridgeService } from "./dashboard-bridge.service";
import { AdvisorWidgetData } from "../../../../shared/advisor-widget/domain/model/advisor-widget.model";
import { DashboardRemote } from "../../infrastructure/dashboard.remote";
import { toConversionMetricsRequest } from "../../shared/conversion-metrics.helper";
import { toDashboardPeriodRequest, toWireScope } from "../../shared/dashboard-request.helper";
import { ConversionMetrics } from "../model/conversion-metrics.model";
import { DashboardDateRange, DashboardPeriod, DashboardScope } from "../model/dashboard-filters.model";
import { DashboardWidgetsData } from "../model/dashboard.model";
import { ExecutiveSnapshot } from "../model/executive-snapshot.model";
import { PartnerScorecard } from "../model/partner-scorecard.model";
import { PipelineHealth } from "../model/pipeline-health.model";
import { SmartInsights } from "../model/smart-insights.model";
import { TrendMetrics } from "../model/trend-metrics.model";

@Injectable({
  providedIn: "root",
})
export class DashboardFacade {
  constructor(
    private readonly bridgeService: DashboardBridgeService,
    private readonly remote: DashboardRemote,
    private readonly brandingService: BrandingService,
    private readonly advisorFacade: AdvisorFacade,
  ) { }

  getCustomerGreeting$(): Observable<string> {
    return this.bridgeService
      .getCurrentBroker$()
      .pipe(map((userData: BrokerUserData) =>
        `${TimeHelper.getGreetingTime(moment())}, ${userData?.firstName ?? userData?.lastName}!`
      ));
  }

  getAdvisorData$(): Observable<AdvisorWidgetData> {
    return this.bridgeService.getBrokerDataDetails$()
      .pipe(
        switchMap((brokerData) => {
          if (brokerData?.advisorId) {
            return this.advisorFacade.getAdvisorWithPhone$(brokerData.advisorId)
              .pipe(map((advisorData) => this.mapToAdvisorWidgetData(advisorData)));
          }
          return combineLatest([this.brandingService.getBranding$()])
            .pipe(map(([brandingData]) => ({
              avatar: null,
              name: null,
              email: null,
              phone: brandingData?.phone,
              calendarUrl: defaultCalendarLinkUrl
            })));
        })
      );
  }

  private mapToAdvisorWidgetData(advisorData: AdvisorWithDisplayPhone | null): AdvisorWidgetData {
    return {
      avatar: advisorData?.avatar,
      name: advisorData?.fullName,
      email: advisorData?.email,
      phone: advisorData?.displayPhone,
      calendarUrl: advisorData?.calendarLinkUrl ?? defaultCalendarLinkUrl
    };
  }

  /** Bar range (inclusive `to`, null for an incomplete custom pair) + scope → the half-open `my|team` wire request; errors reach the screen facade untouched, for its error card. */
  getExecutiveSnapshot$(range: DashboardDateRange | null, scope: DashboardScope): Observable<ExecutiveSnapshot> {
    const request = toDashboardPeriodRequest(range, scope);
    if (!request) {
      return throwError(() => new Error("The executive snapshot needs a resolved date range."));
    }
    return this.remote.getExecutiveSnapshot(request);
  }

  /** Same half-open wire request as the snapshot; errors reach the screen facade untouched, for its error card. */
  getPipelineHealth$(range: DashboardDateRange | null, scope: DashboardScope): Observable<PipelineHealth> {
    const request = toDashboardPeriodRequest(range, scope);
    if (!request) {
      return throwError(() => new Error("Pipeline health needs a resolved date range."));
    }
    return this.remote.getPipelineHealth(request);
  }

  /** The snapshot's half-open wire request plus the preset as `period`; errors reach the screen facade untouched. */
  getConversionMetrics$(
    range: DashboardDateRange | null,
    scope: DashboardScope,
    period: DashboardPeriod,
  ): Observable<ConversionMetrics> {
    const request = toConversionMetricsRequest(range, scope, period);
    if (!request) {
      return throwError(() => new Error("Conversion metrics need a resolved date range."));
    }
    return this.remote.getConversionMetrics(request);
  }

  /** The conversion page's wire request, unchanged; errors reach the screen facade untouched. */
  getTrendMetrics$(
    range: DashboardDateRange | null,
    scope: DashboardScope,
    period: DashboardPeriod,
  ): Observable<TrendMetrics> {
    const request = toConversionMetricsRequest(range, scope, period);
    if (!request) {
      return throwError(() => new Error("Trend metrics need a resolved date range."));
    }
    return this.remote.getTrendMetrics(request);
  }

  /** The conversion page's wire request, unchanged; errors reach the screen facade untouched. */
  getSmartInsights$(
    range: DashboardDateRange | null,
    scope: DashboardScope,
    period: DashboardPeriod,
  ): Observable<SmartInsights> {
    const request = toConversionMetricsRequest(range, scope, period);
    if (!request) {
      return throwError(() => new Error("Smart insights need a resolved date range."));
    }
    return this.remote.getSmartInsights(request);
  }

  /** Scope is the scorecard's only parameter; errors reach the screen facade untouched. */
  getPartnerScorecard$(scope: DashboardScope): Observable<PartnerScorecard> {
    return this.remote.getPartnerScorecard({ scope: toWireScope(scope) });
  }

  getCurrentBrokerUser$(): Observable<BrokerUserData> {
    return this.bridgeService.getCurrentBroker$();
  }

  getDashboardWidgets(onlyActive: boolean = false): Observable<DashboardWidgetsData> {
    return this.remote.getDashboardWidgets().pipe(map(data => {
      if (onlyActive) {
        return {
          mainWidgets: data?.mainWidgets?.filter(widget => widget.active),
          asideWidgets: data?.asideWidgets?.filter(widget => widget.active),
        }
      }

      return data;
    }))
  }
}
